import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, Translate, getPaginationState } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './highest-level-of-qualification.reducer';

export const HighestLevelOfQualification = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const highestLevelOfQualificationList = useAppSelector(state => state.highestLevelOfQualification.entities);
  const loading = useAppSelector(state => state.highestLevelOfQualification.loading);
  const totalItems = useAppSelector(state => state.highestLevelOfQualification.totalItems);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="highest-level-of-qualification-heading" data-cy="HighestLevelOfQualificationHeading">
        <Translate contentKey="apiLocationApp.highestLevelOfQualification.home.title">Highest Level Of Qualifications</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="apiLocationApp.highestLevelOfQualification.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link
            to="/highest-level-of-qualification/new"
            className="btn btn-primary jh-create-entity"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="apiLocationApp.highestLevelOfQualification.home.createLabel">
              Create new Highest Level Of Qualification
            </Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {highestLevelOfQualificationList && highestLevelOfQualificationList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="apiLocationApp.highestLevelOfQualification.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('geographicLevel')}>
                  <Translate contentKey="apiLocationApp.highestLevelOfQualification.geographicLevel">Geographic Level</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geographicLevel')} />
                </th>
                <th className="hand" onClick={sort('date')}>
                  <Translate contentKey="apiLocationApp.highestLevelOfQualification.date">Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('date')} />
                </th>
                <th className="hand" onClick={sort('geography')}>
                  <Translate contentKey="apiLocationApp.highestLevelOfQualification.geography">Geography</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geography')} />
                </th>
                <th className="hand" onClick={sort('geographyCode')}>
                  <Translate contentKey="apiLocationApp.highestLevelOfQualification.geographyCode">Geography Code</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geographyCode')} />
                </th>
                <th className="hand" onClick={sort('totalAllUsualResidents')}>
                  <Translate contentKey="apiLocationApp.highestLevelOfQualification.totalAllUsualResidents">
                    Total All Usual Residents
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('totalAllUsualResidents')} />
                </th>
                <th className="hand" onClick={sort('noQualifications')}>
                  <Translate contentKey="apiLocationApp.highestLevelOfQualification.noQualifications">No Qualifications</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('noQualifications')} />
                </th>
                <th className="hand" onClick={sort('level1Qualifications')}>
                  <Translate contentKey="apiLocationApp.highestLevelOfQualification.level1Qualifications">Level 1 Qualifications</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('level1Qualifications')} />
                </th>
                <th className="hand" onClick={sort('level2Qualifications')}>
                  <Translate contentKey="apiLocationApp.highestLevelOfQualification.level2Qualifications">Level 2 Qualifications</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('level2Qualifications')} />
                </th>
                <th className="hand" onClick={sort('apprenticeship')}>
                  <Translate contentKey="apiLocationApp.highestLevelOfQualification.apprenticeship">Apprenticeship</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('apprenticeship')} />
                </th>
                <th className="hand" onClick={sort('level3Qualifications')}>
                  <Translate contentKey="apiLocationApp.highestLevelOfQualification.level3Qualifications">Level 3 Qualifications</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('level3Qualifications')} />
                </th>
                <th className="hand" onClick={sort('level4QualificationsAndAbove')}>
                  <Translate contentKey="apiLocationApp.highestLevelOfQualification.level4QualificationsAndAbove">
                    Level 4 Qualifications And Above
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('level4QualificationsAndAbove')} />
                </th>
                <th className="hand" onClick={sort('otherQualifications')}>
                  <Translate contentKey="apiLocationApp.highestLevelOfQualification.otherQualifications">Other Qualifications</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('otherQualifications')} />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {highestLevelOfQualificationList.map((highestLevelOfQualification, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/highest-level-of-qualification/${highestLevelOfQualification.id}`} color="link" size="sm">
                      {highestLevelOfQualification.id}
                    </Button>
                  </td>
                  <td>
                    <Translate contentKey={`apiLocationApp.GeographicLevel.${highestLevelOfQualification.geographicLevel}`} />
                  </td>
                  <td>{highestLevelOfQualification.date}</td>
                  <td>{highestLevelOfQualification.geography}</td>
                  <td>{highestLevelOfQualification.geographyCode}</td>
                  <td>{highestLevelOfQualification.totalAllUsualResidents}</td>
                  <td>{highestLevelOfQualification.noQualifications}</td>
                  <td>{highestLevelOfQualification.level1Qualifications}</td>
                  <td>{highestLevelOfQualification.level2Qualifications}</td>
                  <td>{highestLevelOfQualification.apprenticeship}</td>
                  <td>{highestLevelOfQualification.level3Qualifications}</td>
                  <td>{highestLevelOfQualification.level4QualificationsAndAbove}</td>
                  <td>{highestLevelOfQualification.otherQualifications}</td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/highest-level-of-qualification/${highestLevelOfQualification.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/highest-level-of-qualification/${highestLevelOfQualification.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/highest-level-of-qualification/${highestLevelOfQualification.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="apiLocationApp.highestLevelOfQualification.home.notFound">
                No Highest Level Of Qualifications found
              </Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={highestLevelOfQualificationList && highestLevelOfQualificationList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default HighestLevelOfQualification;
